/**
 * Who holds the Leaflet map, and how anyone else gets at it (Phase 1J).
 *
 * `mapBridge.ts` carries one operation — "redraw" — and deliberately knows
 * nothing about Leaflet. This is its companion for the case that operation
 * cannot cover: the four things that need the map OBJECT, not a redraw.
 * `useLaneCostLayer` and `useFacilityCostLayer` add their own overlays to it,
 * and `CostLayerControl.vue` and `FootprintDiffLayer.vue` take it as a prop.
 *
 * It exists so the map can be created by React while those four are still Vue.
 * Today Vue creates it and publishes here, which changes nothing and proves the
 * seam; the next step swaps the publisher for a React hook and leaves every
 * consumer alone.
 *
 * **Not a store, deliberately.** The Zustand stores are mirrored into Pinia,
 * and that mirror hands out a fresh proxy per read — which is fine for rows of
 * data and fatal for a Leaflet map, whose methods compare object identity and
 * whose layers must be the same objects the map holds. So the instance lives in
 * a plain module and is handed out by reference, exactly as created.
 */

/** The Leaflet objects the map is made of. Typed as unknown: nothing here imports Leaflet. */
export type MapInstance = {
  map: unknown
  facilitiesLayer: unknown
  lanesLayer: unknown
  /**
   * The element Leaflet was mounted into.
   *
   * Published because two things need the ELEMENT rather than the map: the
   * node-placement modes set a crosshair cursor on it, and the report
   * generator screenshots it. Both reached it through the component that
   * rendered it, which is the coupling this seam exists to remove.
   */
  container: unknown
}

type Listener = (instance: MapInstance | null) => void

let instance: MapInstance | null = null
const listeners = new Set<Listener>()

/**
 * Publish the map, or `null` when it is torn down.
 *
 * Whoever creates the map calls this — `useMapState` today, a React hook next.
 * Publishing null on teardown matters: a consumer holding a removed Leaflet map
 * will throw on the next call, and "the map is gone" is a state they can
 * handle, while a stale reference is not.
 */
export function setMapInstance(next: MapInstance | null) {
  instance = next
  // A copy, so a listener that unsubscribes while being notified does not
  // make the set mutate mid-iteration.
  for (const listener of [...listeners]) notify(listener, next)
}

/**
 * One bad consumer must not stop the others hearing about the map, and must
 * not fail the map's own creation — nor its own registration.
 */
function notify(listener: Listener, next: MapInstance | null) {
  try {
    listener(next)
  } catch (error) {
    console.error('[map instance] a listener threw', error)
  }
}

/**
 * Hear about the map, now and whenever it changes.
 *
 * Called back IMMEDIATELY with the current value, so a consumer that mounts
 * after the map was created does not wait for a change that already happened —
 * which is the ordering bug this shape is meant to remove.
 */
export function onMapInstance(listener: Listener): () => void {
  listeners.add(listener)
  notify(listener, instance)
  return () => {
    listeners.delete(listener)
  }
}

/** Test-only: forget the map and every listener. */
export function __resetMapInstance() {
  instance = null
  listeners.clear()
}
